import { useState, useEffect } from "react";
import { Modal, Button, Form, Input, Select, InputNumber, Switch, Tooltip, Spin } from "antd";
import {
  getCopilotChatCategoryById,
  updateCopilotChatCategoryById,
} from "@api/copilots/browser-extension/chat/copilotChatCategory";
import { SuccessNotification, ErrorNotification } from "@components";

const { TextArea } = Input;

// Common emoji icons for categories
const EMOJI_OPTIONS = [
  "📁", "⚡", "💼", "🎨", "💻", "💬", "📚", "📣", "🎯", "📈",
  "📊", "👥", "🔧", "🚀", "💡", "🌍", "🔬", "✍️", "🎪", "🏷️"
];

// Common color options
const COLOR_OPTIONS = [
  "#f59e0b", "#3b82f6", "#ec4899", "#10b981", "#8b5cf6", "#06b6d4",
  "#ef4444", "#f97316", "#14b8a6", "#6366f1", "#a855f7", "#0ea5e9"
];

const initialState = {
  name: "",
  icon: "📁",
  color: "#6366f1",
  description: "",
  order: 0,
  isActive: true,
};

const CopilotChatCategoryUpdateModal = ({
  open,
  handleModalClick,
  fetchCategories,
  categoryId,
}) => {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  // Fetch category data
  useEffect(() => {
    const fetchCategory = async () => {
      if (!categoryId) return;

      setFetchLoading(true);
      try {
        const { data } = await getCopilotChatCategoryById(categoryId);
        setValues({
          name: data.name || "",
          icon: data.icon || "📁",
          color: data.color || "#6366f1",
          description: data.description || "",
          order: data.order || 0,
          isActive: data.isActive !== undefined ? data.isActive : true,
        });
      } catch (error) {
        ErrorNotification("Error fetching category data");
      } finally {
        setFetchLoading(false);
      }
    };

    if (open && categoryId) {
      fetchCategory();
    }
  }, [open, categoryId]);

  // Handle form change
  const handleFormChange = (e) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // Handle update category
  const handleUpdate = async () => {
    if (!values.name.trim()) {
      return ErrorNotification("Please enter a category name");
    }

    setLoading(true);
    try {
      await updateCopilotChatCategoryById(categoryId, values);
      setValues(initialState);
      handleModalClick();
      fetchCategories();
      SuccessNotification("Category updated successfully");
    } catch (error) {
      console.error("Error updating category:", error);
      ErrorNotification(
        error.response?.data?.message || "Error updating category"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
            Update Category
          </h2>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: "13px" }}>
            Edit the category details
          </p>
        </div>
      }
      open={open}
      onCancel={handleModalClick}
      width={600}
      centered
      footer={[
        <Button key="back" onClick={handleModalClick}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleUpdate}
          loading={loading}
        >
          Update Category
        </Button>,
      ]}
    >
      {fetchLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <Form layout="vertical" style={{ marginTop: 16 }}>
          {/* Name */}
          <Form.Item label="Category Name" required>
            <Input
              name="name"
              value={values.name}
              onChange={handleFormChange}
              placeholder="e.g., Productivity, Professional, Creative"
              maxLength={50}
            />
          </Form.Item>

          {/* Icon & Color Row */}
          <div style={{ display: "flex", gap: "16px" }}>
            <Form.Item label="Icon" style={{ flex: 1 }}>
              <Select
                value={values.icon}
                onChange={(value) => setValues({ ...values, icon: value })}
                dropdownRender={() => (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(10, 1fr)",
                      gap: "4px",
                      padding: "8px",
                    }}
                  >
                    {EMOJI_OPTIONS.map((emoji) => (
                      <div
                        key={emoji}
                        onClick={() => setValues({ ...values, icon: emoji })}
                        style={{
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          borderRadius: "4px",
                          fontSize: "18px",
                          backgroundColor:
                            values.icon === emoji ? "#e6f7ff" : "transparent",
                          border:
                            values.icon === emoji
                              ? "1px solid #1890ff"
                              : "1px solid transparent",
                        }}
                      >
                        {emoji}
                      </div>
                    ))}
                  </div>
                )}
              >
                <Select.Option value={values.icon}>
                  <span style={{ fontSize: "18px" }}>{values.icon}</span>
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Color" style={{ flex: 1 }}>
              <Select
                value={values.color}
                onChange={(value) => setValues({ ...values, color: value })}
                dropdownRender={() => (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(6, 1fr)",
                      gap: "6px",
                      padding: "8px",
                    }}
                  >
                    {COLOR_OPTIONS.map((color) => (
                      <Tooltip title={color} key={color}>
                        <div
                          onClick={() =>
                            setValues({ ...values, color: color })
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "4px",
                            backgroundColor: color,
                            cursor: "pointer",
                            border:
                              values.color === color
                                ? "2px solid #1890ff"
                                : "2px solid transparent",
                          }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                )}
              >
                <Select.Option value={values.color}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "3px",
                        backgroundColor: values.color,
                      }}
                    />
                    <span>{values.color}</span>
                  </div>
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Order" style={{ width: "100px" }}>
              <InputNumber
                value={values.order}
                onChange={(value) => setValues({ ...values, order: value || 0 })}
                min={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          {/* Description */}
          <Form.Item label="Description">
            <TextArea
              name="description"
              value={values.description}
              onChange={handleFormChange}
              placeholder="Brief description of what this category contains"
              rows={3}
              maxLength={200}
              showCount
            />
          </Form.Item>

          {/* Active Status */}
          <Form.Item label="Active Status">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Switch
                checked={values.isActive}
                onChange={(checked) =>
                  setValues({ ...values, isActive: checked })
                }
              />
              <span style={{ color: "#666", fontSize: "13px" }}>
                {values.isActive
                  ? "Category is visible to users"
                  : "Category is hidden from users"}
              </span>
            </div>
          </Form.Item>

          {/* Preview */}
          <Form.Item label="Preview">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "16px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                border: "1px solid #e8e8e8",
                opacity: values.isActive ? 1 : 0.5,
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  backgroundColor: values.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                }}
              >
                {values.icon}
              </div>
              <div>
                <h4 style={{ margin: 0, fontWeight: 600 }}>
                  {values.name || "Category Name"}
                </h4>
                <p style={{ margin: 0, color: "#666", fontSize: "13px" }}>
                  {values.description || "Category description will appear here"}
                </p>
              </div>
            </div>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default CopilotChatCategoryUpdateModal;
